/**
 * Composable for background token refresh
 * Calls /api/auth/refresh-openid to keep access tokens fresh
 * Prevents work loss from idle timeout
 * Also preemptively refreshes tokens before they expire to prevent white page flashes
 */

export const useTokenRefresh = () => {
  const config = useRuntimeConfig();
  const auth = useAuth();

  // State
  let preemptiveRefreshTimeoutId: NodeJS.Timeout | null = null;
  let isRefreshing = false;
  const PREEMPTIVE_REFRESH_BUFFER = 30 * 1000; // Refresh 30 seconds before expiration
  const MAX_RETRIES = 3;
  let retryCount = 0;

  /**
   * Schedules a preemptive refresh based on token expiration time
   */
  const schedulePreemptiveRefresh = (accessExpiresAt?: number, refreshExpiresAt?: number) => {
    // Clear any existing preemptive refresh timeout
    if (preemptiveRefreshTimeoutId) {
      clearTimeout(preemptiveRefreshTimeoutId);
      preemptiveRefreshTimeoutId = null;
    }

    try {
      const expirationTime = accessExpiresAt ?? refreshExpiresAt;
      if (!expirationTime) {
        return;
      }

      const now = Date.now();
      const timeUntilExpiry = expirationTime - now;

      // Only schedule if token expires in the future
      if (timeUntilExpiry > 0) {
        const preemptiveRefreshTime = timeUntilExpiry - PREEMPTIVE_REFRESH_BUFFER;

        if (preemptiveRefreshTime > 0) {
          const basis = accessExpiresAt ? 'access' : 'refresh';
          console.log(`[TokenRefresh] Preemptive refresh scheduled in ${Math.round(preemptiveRefreshTime / 1000)} seconds (${basis} token expires in ${Math.round(timeUntilExpiry / 1000)} seconds)`);

          preemptiveRefreshTimeoutId = setTimeout(() => {
            console.log('[TokenRefresh] Performing preemptive token refresh');
            performRefresh(true);
          }, preemptiveRefreshTime);
        } else {
          // Token expires soon, refresh immediately
          console.log('[TokenRefresh] Token expiring very soon, refreshing immediately');
          performRefresh(true);
        }
      }
    } catch (error) {
      console.error('[TokenRefresh] Error scheduling preemptive refresh:', error);
    }
  };

  /**
   * Calls the backend refresh endpoint
   * Since this is BFF, tokens are in httpOnly cookies and handled server-side
   */
  const performRefresh = async (isImmediate = false): Promise<boolean> => {
    // Prevent concurrent refresh calls
    if (isRefreshing) {
      return false;
    }

    if (!auth.loadingSessionProvider && auth.sessionProvider === null) {
      await auth.fetchSessionProvider();
    }

    const configuredMode = (config.public as any).AUTH_MODE ?? "keycloak";
    const wantsKeycloak = auth.sessionProvider
      ? auth.sessionProvider === "keycloak"
      : configuredMode === "keycloak";
    if (!wantsKeycloak) {
      console.debug('[TokenRefresh] Session provider is local, skipping token refresh');
      return true;
    }

    isRefreshing = true;

    try {
      const apiBase = (config.public as any).BACKEND_URL ?? "/api";
      const response = await fetch(`${apiBase}/auth/refresh-openid`, {
        method: 'GET',
        credentials: 'include', // Include httpOnly cookies
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        retryCount = 0; // Reset retry count on success
        if (isImmediate) {
          console.log('[TokenRefresh] Immediate refresh successful');
        } else {
          console.log('[TokenRefresh] Refresh successful');
        }

        let refreshExpiresAt: number | undefined;
        let accessExpiresAt: number | undefined;
        try {
          const data = await response.clone().json();
          if (typeof data?.accessExpiresAt === 'number') {
            accessExpiresAt = data.accessExpiresAt;
          } else if (typeof data?.expiresIn === 'number') {
            accessExpiresAt = Date.now() + data.expiresIn * 1000;
          } else if (typeof data?.accessExpiresIn === 'number') {
            accessExpiresAt = Date.now() + data.accessExpiresIn * 1000;
          }

          if (typeof data?.refreshExpiresAt === 'number') {
            refreshExpiresAt = data.refreshExpiresAt;
          } else if (typeof data?.refreshExpiresIn === 'number') {
            refreshExpiresAt = Date.now() + data.refreshExpiresIn * 1000;
          }
        } catch (parseError) {
          console.warn('[TokenRefresh] Failed to parse refresh expiry:', parseError);
        }

        // Schedule the next preemptive refresh based on access expiry (fallback to refresh expiry)
        schedulePreemptiveRefresh(accessExpiresAt, refreshExpiresAt);

        return true;
      } else if (response.status === 204) {
        // No refresh token / non-keycloak session
        stopRefresh();
        return true;
      } else if (response.status === 401) {
        // Token truly expired, need to log in again
        console.warn('[TokenRefresh] Refresh failed with 401 - Session expired');
        stopRefresh();

        // Clear authentication state
        auth.user = null;

        const route = useRoute();
        const authMode = (config.public as any).AUTH_MODE ?? "keycloak";
        if (authMode === "keycloak") {
          auth.startKeycloakLogin(route.fullPath);
          return false;
        }
        if (route.path !== "/login") {
          navigateTo({ path: "/login", query: { returnTo: route.fullPath } });
        }
        return false;
      } else {
        // Other error status
        console.error(`[TokenRefresh] Refresh failed with status ${response.status}`);
        return false;
      }
    } catch (error) {
      console.error('[TokenRefresh] Error during token refresh:', error);

      // Exponential backoff retry logic for transient failures
      if (retryCount < MAX_RETRIES && !isImmediate) {
        retryCount++;
        const backoffMs = Math.pow(2, retryCount) * 1000; // 2s, 4s, 8s
        console.log(`[TokenRefresh] Retrying refresh in ${backoffMs}ms (attempt ${retryCount}/${MAX_RETRIES})`);
        setTimeout(() => {
          performRefresh(false);
        }, backoffMs);
      } else if (!isImmediate) {
        // Failed after retries, clear session
        console.error('[TokenRefresh] Max retries exceeded, redirecting to login');
        stopRefresh();
        const route = useRoute();
        const authMode = (config.public as any).AUTH_MODE ?? "keycloak";
        if (authMode === "keycloak") {
          auth.startKeycloakLogin(route.fullPath);
          return false;
        }
        if (route.path !== "/login") {
          navigateTo({ path: "/login", query: { returnTo: route.fullPath } });
        }
      }
      return false;
    } finally {
      isRefreshing = false;
    }
  };

  /**
   * Start the preemptive refresh loop
   */
  const startRefresh = async () => {
    if (!auth.loadingSessionProvider && auth.sessionProvider === null) {
      await auth.fetchSessionProvider();
    }
    const configuredMode = (config.public as any).AUTH_MODE ?? "keycloak";
    const wantsKeycloak = auth.sessionProvider
      ? auth.sessionProvider === "keycloak"
      : configuredMode === "keycloak";
    if (!wantsKeycloak) {
      console.debug('[TokenRefresh] Session provider is local, not starting refresh loop');
      return;
    }
    if (preemptiveRefreshTimeoutId) {
      return; // Already running
    }

    console.log('[TokenRefresh] Starting preemptive token refresh scheduling');

    // Initial refresh after a short delay to ensure app is fully initialized
    setTimeout(() => {
      performRefresh(false);
    }, 1000);
  };

  /**
   * Stop the preemptive refresh loop
   */
  const stopRefresh = () => {
     if (preemptiveRefreshTimeoutId) {
       clearTimeout(preemptiveRefreshTimeoutId);
       preemptiveRefreshTimeoutId = null;
       console.log('[TokenRefresh] Stopped preemptive refresh timeout');
     }
  };

  /**
   * Trigger an immediate refresh (e.g., when a 401 error is detected)
   */
  const refreshNow = async () => {
    console.log('[TokenRefresh] Triggering immediate refresh');
    return performRefresh(true);
  };

  // Cleanup on unmount
  onUnmounted(() => {
    stopRefresh();
  });

  return {
    startRefresh,
    stopRefresh,
    refreshNow,
    isRefreshing: readonly(ref(false)), // For monitoring if needed
  };
};
